import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';

import { useResetCohortBuilderState } from '../common/useResetCohortBuilderState';

// TODO: simplify with analytical_variables_in_cohort_builder ff cleanup
// Version navigation is handled by useCohortDefinitionSocket (silent replaceState).
// The latestCohortVersionId redirect (lines 76-98) can likely be removed.
export const useRedirectOnInvalidParams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageTab = searchParams.get('pageTab');
  const {
    projectId,
    cohortId,
    cohortVersionId,
    data,
    isCohortSuccess,
    isProjectSuccess,
    latestCohortVersionId,
    projectData,
    projectError,
  } = useCohortBuilderData();
  const { resetCohortBuilderState } = useResetCohortBuilderState();
  // OR-6089: under gen-3 we mount on the new `cohort/` (singular) route
  // and the URL's version segment can intentionally lag the cohort's
  // current `latestCohortVersionId` (agent upserts a draft on every
  // edit — the silent ``replaceState`` in ``useVersionNavigation``
  // catches up the URL without bouncing the user). The legacy
  // "redirect to latest version" block below builds a
  // ``getCohortBuilderPath`` URL (``/cohorts/.../cohort-builder``),
  // which would round-trip every gen-3 user to the legacy route on
  // refresh and on every agent edit. Gate that single block off when
  // gen-3 is enabled; the other redirects (missing project / cohort)
  // remain on so the not-found UX is preserved.
  // TODO: cleanup with research_assistant_gen_3 ff — delete the
  // latest-version block entirely once the flag rolls out (the
  // socket's silent replaceState owns this contract; see the file
  // header TODO).
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;

  const [hasRedirected, setHasRedirected] = useState(false);

  // Redirect to cohorts path if cohort is not found
  useEffect(() => {
    if (hasRedirected) return;

    if (!projectId) {
      console.info('No projectId found. Redirecting to projects base path.');
      setHasRedirected(true);
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (!cohortId) {
      console.info('No cohortId found. Redirecting to cohorts path.');
      setHasRedirected(true);
      navigate(getCohortsPath(projectId));
      resetCohortBuilderState();
      return;
    }

    if (isProjectSuccess && !projectData?.data) {
      console.info('Project data not found. Redirecting to projects base path.');
      setHasRedirected(true);
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (projectError) {
      console.info('Project error encountered. Redirecting to projects base path.');
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (isCohortSuccess && !data?.data) {
      console.info('Cohort data not found. Redirecting to cohorts path.');
      setHasRedirected(true);
      navigate(getCohortsPath(projectId));
      resetCohortBuilderState();
      return;
    }

    // Redirect to latest version if URL version doesn't match.
    // Skipped under gen-3 — see the comment by ``isGen3Enabled``.
    if (
      !isGen3Enabled &&
      isCohortSuccess &&
      data?.data &&
      cohortVersionId !== latestCohortVersionId &&
      latestCohortVersionId
    ) {
      // Check if the actual browser URL already matches the latest version (Silent Update case)
      // If so, do NOT trigger a React Router navigation, as that would reset the page state.
      const actualPath = window.location.pathname;
      if (actualPath.includes(`/cohort-version/${latestCohortVersionId}`)) {
        setHasRedirected(true);
        return;
      }

      setHasRedirected(true);
      navigate(
        `${getCohortBuilderPath(projectId, cohortId, latestCohortVersionId)}?pageTab=${
          pageTab || 'events'
        }`,
        { replace: true }
      );
      return;
    }
  }, [
    cohortId,
    cohortVersionId,
    data?.data,
    hasRedirected,
    isCohortSuccess,
    isGen3Enabled,
    isProjectSuccess,
    latestCohortVersionId,
    navigate,
    pageTab,
    projectData?.data,
    projectError,
    projectId,
    resetCohortBuilderState,
  ]);
};
